import React, { useState } from 'react'

/**
 * RatingBars — five horizontal bars, heat-mapped from rust (1) to moss (5).
 * The peak bar is solid; trailing bars are the same hue at 40% color-mix.
 *
 * Interactive mode (`onChange` provided) shows a dashed-outline hover
 * preview while the pointer is over a bar; commit only on click. Read-only
 * mode just renders the score.
 *
 * The commit-then-reveal mechanic lives in `<RevealCover>` and the parent
 * card — pass `peek` to render the bars without a cover (for storybook /
 * read-only contexts).
 *
 * Spec: claude_design/design-system/primitives.html §8.4
 */
export function RatingBars({
  value = 0,
  onChange,
  ariaLabel = 'Rating',
  className = '',
}) {
  const [hover, setHover] = useState(0)
  const interactive = typeof onChange === 'function'
  const previewValue = hover || value
  return (
    <div
      className={['rb', interactive && 'rb--interactive', className].filter(Boolean).join(' ')}
      role={interactive ? 'radiogroup' : 'group'}
      aria-label={ariaLabel}
    >
      {[1, 2, 3, 4, 5].map((n) => {
        const isPeak = n === previewValue
        const isFill = n < previewValue
        const isHoverPreview = interactive && hover > 0 && n <= hover && hover !== value
        const cls = [
          'rb__bar',
          isPeak && `rb__bar--peak-${n}`,
          isFill && `rb__bar--fill-${previewValue}`,
          isHoverPreview && 'rb__bar--hover',
        ].filter(Boolean).join(' ')
        const Tag = interactive ? 'button' : 'div'
        const props = interactive
          ? {
              type: 'button',
              role: 'radio',
              'aria-checked': n === value,
              'aria-label': `${n}`,
              onClick: () => onChange(n),
              onMouseEnter: () => setHover(n),
              onMouseLeave: () => setHover(0),
              onFocus: () => setHover(n),
              onBlur: () => setHover(0),
            }
          : {}
        return <Tag key={n} className={cls} {...props} />
      })}
    </div>
  )
}

/**
 * RevealCover — the hatched cover that hides RatingBars until both parties
 * commit. Stamp `revealed=true` only when the server returns `revealedAt`;
 * the cover animates off with the three-beat choreography (cover lift →
 * +150ms content fade-up → +550ms note slide).
 *
 * Spec: claude_design/design-system/motion.html · the "reveal" pattern
 */
export function RevealCover({ revealed = false, children, label = 'awaiting' }) {
  return (
    <div className={['rb-reveal', revealed && 'rb-reveal--revealed'].filter(Boolean).join(' ')}>
      <div className="rb-reveal__content" aria-hidden={!revealed}>{children}</div>
      <div className="rb-reveal__cover" aria-hidden={revealed} role="status">
        <span className="rb-reveal__label">{label}</span>
      </div>
    </div>
  )
}

/**
 * RatingNote — italic-serif note on a sunken background with a 2px left
 * accent. Clay for the report, berry for the manager. The accent is the
 * only signal of authorship; never put a name label in front of the note.
 */
export function RatingNote({ author = 'report', children, ...rest }) {
  return (
    <div className={`rb-note rb-note--${author}`} {...rest}>
      {children}
    </div>
  )
}
